import { useEffect, useState, useCallback } from 'react';
import { format, addWeeks, subWeeks, eachDayOfInterval, getISOWeek, isSameDay } from 'date-fns';
import { nl } from 'date-fns/locale';
import { AlarmClock, CalendarDays, ChevronLeft, ChevronRight, Clock3, MapPin, Tag, UserRound, UsersRound, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { toAmsterdamTime, toAmsterdamDate, weekBounds } from '../../utils/dateUtils.js';
import { lesCoachNaam } from '../../utils/lesCoachUtils.js';
import { getActiveClientMemberships, membershipDisplayName, primaryMembershipCredits } from '../../utils/membershipUtils.js';
import { getClientLessonDetail, trainingLocationLines } from '../../utils/trainingDetails.js';

// eslint-disable-next-line no-unused-vars
function DescriptionTooltip({ description }) {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: 4, verticalAlign: 'middle' }}>
      <span
        style={{ cursor: 'pointer', fontSize: 12, color: 'var(--color-text-muted)', userSelect: 'none' }}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onClick={() => setVisible(v => !v)}
        title={description}
      >
        ℹ
      </span>
      {visible && (
        <div style={{
          position: 'absolute',
          bottom: '120%',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1a1a2e',
          color: '#fff',
          padding: '6px 10px',
          borderRadius: 6,
          fontSize: 11,
          minWidth: 220,
          maxWidth: 320,
          zIndex: 200,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          whiteSpace: 'normal',
          lineHeight: 1.4,
          pointerEvents: 'none',
        }}>
          {description}
        </div>
      )}
    </span>
  );
}

export default function ClientRoosterPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [weekBase, setWeekBase] = useState(new Date());
  const [lessons, setLessons] = useState([]);
  const [myEnrollments, setMyEnrollments] = useState({});
  const [enrollmentCounts, setEnrollmentCounts] = useState({});
  const [creditMemberships, setCreditMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [pendingEnrollments, setPendingEnrollments] = useState([]);
  const [pendingActionId, setPendingActionId] = useState(null);
  const [pendingError, setPendingError] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const { start, end } = weekBounds(weekBase);

  const loadPendingEnrollments = useCallback(async () => {
    if (!user?.id) return;
    const now = new Date().toISOString();
    setPendingError('');
    try {
      const { data, error: err } = await supabase
        .from('lesson_enrollments')
        .select(`
          id, lesson_id,
          group_lessons!inner(id, name, starts_at, duration_minutes,
            locations(name),
            coach:coach_basic_profiles!group_lessons_coach_id_fkey(full_name),
            executing_coach:coach_basic_profiles!group_lessons_executing_coach_id_fkey(full_name))
        `)
        .eq('client_id', user.id)
        .eq('status', 'pending_confirmation')
        .gt('group_lessons.starts_at', now)
        .order('group_lessons(starts_at)', { ascending: true });
      if (err) throw err;
      setPendingEnrollments(data || []);
    } catch (err) {
      // Zelfde fix als in de app (TrainingenScreen, 2026-07-23):
      // een stille catch verborg dat de cliënt zijn openstaande uitnodigingen
      // niet zag — de sectie rendert alleen bij lengte > 0, dus een mislukte
      // load was niet van "geen uitnodigingen" te onderscheiden.
      console.error('Fout bij laden openstaande uitnodigingen:', err);
      setPendingError(t('clientRoosterUitnodigingen.foutLaden') + err.message);
    }
  }, [user?.id]);

  async function handleConfirm(enrollmentId) {
    setPendingActionId(enrollmentId);
    try {
      const { error: err } = await supabase.rpc('confirm_enrollment', {
        p_enrollment_id: enrollmentId,
      });
      if (err) throw err;
      showSuccess(t('clientRoosterUitnodigingen.bevestigdSucces'));
      await Promise.all([loadPendingEnrollments(), loadLessons()]);
    } catch (err) {
      showError(t('clientRoosterUitnodigingen.foutBevestigen') + err.message);
    } finally {
      setPendingActionId(null);
    }
  }

  async function handleDecline(enrollmentId) {
    setPendingActionId(enrollmentId);
    try {
      const { error: err } = await supabase.rpc('decline_enrollment', {
        p_enrollment_id: enrollmentId,
      });
      if (err) throw err;
      showSuccess(t('clientRoosterUitnodigingen.afgewezenSucces'));
      await Promise.all([loadPendingEnrollments(), loadLessons()]);
    } catch (err) {
      showError(t('clientRoosterUitnodigingen.foutAfwijzen') + err.message);
    } finally {
      setPendingActionId(null);
    }
  }

  // FASE 2 (2026-08-06): deze pagina deed drie losse queries — een kale
  // `group_lessons`-weekquery plus twee `lesson_enrollments`-queries — en had
  // daarmee twee bevestigde bugs:
  //
  //  1. GEEN FILTER OP `type`. `lessons_select` laat een PT-les door via
  //     `client_id = auth.uid()`, dus eigen PT-afspraken renderden hier als
  //     gewone groepsles, mét deelnemersaantallen en in-/uitschrijfknoppen die
  //     op PT niet van toepassing zijn (`type` werd niet eens opgehaald).
  //  2. KAPOTTE DEELNEMERSTELLER sinds de H1-privacyfix van 2026-08-03. De
  //     telquery selecteerde `lesson_enrollments` zonder `client_id`-filter,
  //     maar `enrollments_select_own` levert een cliënt uitsluitend de eigen
  //     rij — de teller stond dus feitelijk altijd op 0 of 1. Live gemeten op
  //     2026-08-06: een groepsles met 3 ingeschreven cliënten gaf hier 1.
  //
  // Beide verdwijnen door op `get_client_training_agenda` over te schakelen:
  // die levert `training_kind` én een server-side geaggregeerde
  // `enrolled_count` (SECURITY DEFINER, mét leesbaarheidscheck). Als derde
  // gevolg ziet de tweede cliënt van een PT-plus-afspraak zijn afspraak nu ook
  // in de webportal — de RPC autoriseert op `lesson_enrollments`.
  //
  // Alle vier de vensters staan op dezelfde week: deze pagina toont één week
  // en kent geen apart "eigen"-venster zoals de mobiele app.
  const loadLessons = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const startISO = start.toISOString();
      const endISO = new Date(end.getTime() + 24 * 3600 * 1000).toISOString();

      const [agendaRes, creditsRes] = await Promise.all([
        supabase.rpc('get_client_training_agenda', {
          p_client_id: user.id,
          p_from: startISO,
          p_until: endISO,
          p_own_from: startISO,
          p_own_until: endISO,
          p_include_available: true,
        }),
        // Fase 3 (2026-08-09): het saldo kwam uit de dode legacy-kolom
        // current_credits en stond daardoor structureel op 0. Nu de som van
        // alle nog-geldige batches over de actieve abonnementen van deze
        // cliënt — dit is een totaalbadge, geen abonnementsweergave.
        getActiveClientMemberships(user.id).catch(() => []),
      ]);

      if (agendaRes.error) throw agendaRes.error;

      // Een aanvraag zonder gematerialiseerde les hoort ook in het weekrooster:
      // ze heeft een concreet moment (`COALESCE(reschedule_proposed_at,
      // requested_at)`) en de cliënt moet kunnen zien dat er iets op die dag
      // staat. Ze krijgt hieronder een eigen, knoploze weergave.
      const data = (agendaRes.data || []).filter(row =>
        !row.location || row.location.is_active !== false
      );

      setLessons(data);
      setCreditMemberships(creditsRes || []);

      // Tellingen en eigen status komen mee uit dezelfde rij; er zijn geen
      // losse enrollment-queries meer.
      const counts = {};
      const myMap = {};
      data.forEach(row => {
        if (row.lesson_id) counts[row.lesson_id] = row.enrolled_count ?? 0;
        if (row.my_status) myMap[row.id] = row.my_status;
      });
      setEnrollmentCounts(counts);
      setMyEnrollments(myMap);
    } catch (err) {
      setError(t('clientRooster.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id, start.toISOString(), end.toISOString()]);

  useEffect(() => {
    loadLessons();
    loadPendingEnrollments();
    // Check blokkering
    if (user?.id) {
      supabase.from('profiles').select('blocked_at').eq('id', user.id).maybeSingle()
        .then(({ data }) => setIsBlocked(!!data?.blocked_at));
    }
  }, [loadLessons, loadPendingEnrollments]);

  async function handleEnroll(lessonId) {
    setActionLoading(lessonId);
    try {
      const { data, error: rpcErr } = await supabase.rpc('enroll_client', { p_lesson_id: lessonId });
      if (rpcErr) throw rpcErr;

      if (data?.error) {
        const errMap = {
          les_niet_gevonden: t('clientRooster.foutLesBestaat'),
          geen_lidmaatschap: t('clientRooster.foutGeenLidmaatschap'),
          onvoldoende_credits: t('clientRooster.foutOnvoldoendeCredits'),
          abonnement_niet_toegestaan: t('clientRooster.foutAbonnementNietToegestaan'),
        };
        showError(errMap[data.error] || t('clientRooster.foutInschrijven') + data.error);
      } else if (data?.status === 'waitlisted') {
        showSuccess(t('clientRooster.wachtlijstGeplaatst'));
        await loadLessons();
      } else {
        showSuccess(t('clientRooster.ingeschrevenSucces'));
        await loadLessons();
      }
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('Account geblokkeerd')) {
        showError(t('clientRooster.foutAccountGeblokkeerd'));
      } else if (msg.includes('verleden')) {
        showError(t('clientRooster.foutLesVerleden'));
      } else {
        showError(t('clientRooster.foutInschrijven') + msg);
      }
    } finally {
      setActionLoading(null);
    }
  }

  async function handleUnenroll(lessonId) {
    setActionLoading(lessonId);
    try {
      const { data, error: rpcErr } = await supabase.rpc('unenroll_client', { p_lesson_id: lessonId });
      if (rpcErr) throw rpcErr;

      if (data?.error) {
        const errMap = {
          niet_ingeschreven: t('clientRooster.foutNietIngeschreven'),
          deadline_verstreken: t('clientRooster.foutDeadlineVerstreken'),
        };
        showError(errMap[data.error] || t('clientRooster.foutUitschrijven') + data.error);
      } else {
        showSuccess(t('clientRooster.uitgeschrevenSucces'));
        await loadLessons();
      }
    } catch (err) {
      showError(t('clientRooster.foutUitschrijven') + err.message);
    } finally {
      setActionLoading(null);
    }
  }

  const days = eachDayOfInterval({ start, end });
  const now = new Date();

  function lessonsForDay(day) {
    return lessons.filter(l => isSameDay(toAmsterdamDate(l.starts_at), day));
  }

  async function openLessonDetails(lesson) {
    setSelectedLesson(lesson);
    setDetailError('');
    if (!lesson.lesson_id || lesson.is_request) return;
    setDetailLoading(true);
    try {
      const detail = await getClientLessonDetail(lesson.lesson_id, user.id);
      if (!detail) throw new Error('not_found');
      setSelectedLesson(current => current?.id === lesson.id ? { ...lesson, ...detail } : current);
    } catch (err) {
      console.error('Trainingsdetails laden mislukt:', err);
      setDetailError(t('clientRooster.detailsFout'));
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <div className="client-rooster-page">
      {isBlocked && (
        <div style={{ background: '#dc262622', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <p style={{ color: '#dc2626', fontWeight: 600, margin: 0, textAlign: 'center' }}>
            {t('clientRooster.accountGeblokkeerd')}
          </p>
        </div>
      )}
      <div className="page-header client-rooster-header">
        <div className="client-rooster-heading">
          <div className="client-rooster-week-number">
            <span>{t('clientRooster.week')}</span>
            <strong>{getISOWeek(start)}</strong>
          </div>
          <div>
            <h1 className="page-title">{t('clientRooster.title')}</h1>
            <p className="page-subtitle client-rooster-subtitle">
              {format(start, 'd MMMM', { locale: nl })} {t('clientRooster.tm')} {format(end, 'd MMMM yyyy', { locale: nl })}
            </p>
          </div>
        </div>
        <div className="client-rooster-nav">
          <button className="btn btn-ghost client-rooster-nav-arrow" onClick={() => setWeekBase(w => subWeeks(w, 1))} aria-label={t('clientRooster.vorigeWeek')}>
            <ChevronLeft size={18} />
          </button>
          <button className="btn btn-ghost" onClick={() => setWeekBase(new Date())}>
            {t('clientRooster.vandaag')}
          </button>
          <button className="btn btn-ghost client-rooster-nav-arrow" onClick={() => setWeekBase(w => addWeeks(w, 1))} aria-label={t('clientRooster.volgendeWeek')}>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {creditMemberships.length > 0 && (
        <div className="client-rooster-credit-overview" aria-label={t('clientRooster.creditsPerAbonnement')}>
          <span className="client-rooster-credit-label">{t('clientRooster.creditsPerAbonnement')}</span>
          <div>
            {creditMemberships.map(membership => (
              <span className="client-rooster-credit-pill" key={membership.id}>
                <strong>{primaryMembershipCredits(membership)}</strong>
                {membershipDisplayName(membership, t('clientRooster.onbekendAbonnement'))}
              </span>
            ))}
          </div>
        </div>
      )}

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {/* Openstaande uitnodigingen */}
      {pendingError && (
        <div className="error-msg" style={{ marginBottom: 20 }}>{pendingError}</div>
      )}
      {pendingEnrollments.length > 0 && (
        <div style={{
          border: '2px solid #d97706', borderRadius: 'var(--radius)',
          padding: 20, marginBottom: 24,
          background: 'rgba(217,119,6,0.04)',
        }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: '#d97706',
            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14,
          }}>
            {t('clientRoosterUitnodigingen.titel')} ({pendingEnrollments.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pendingEnrollments.map(enrollment => {
              const les = enrollment.group_lessons;
              const datum = format(toAmsterdamDate(les.starts_at), 'EEEE d MMMM yyyy', { locale: nl });
              const tijd = toAmsterdamTime(les.starts_at);
              const isBusy = pendingActionId === enrollment.id;
              return (
                <div key={enrollment.id} style={{
                  padding: '12px 16px',
                  background: 'var(--color-surface)',
                  borderRadius: 8,
                  border: '1px solid var(--color-border)',
                  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--color-text)', marginBottom: 2 }}>
                      {les.name}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                      {datum} · {tijd} · {les.duration_minutes} min
                    </div>
                    {les.locations?.name && (
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                        📍 {les.locations.name}
                      </div>
                    )}
                    {!!lesCoachNaam(les) && (
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                        👤 {lesCoachNaam(les)}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleConfirm(enrollment.id)}
                      disabled={isBusy}
                      style={{ minWidth: 100 }}
                    >
                      {isBusy ? '…' : t('clientRoosterUitnodigingen.bevestigen')}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleDecline(enrollment.id)}
                      disabled={isBusy}
                      style={{ minWidth: 80, color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                    >
                      {isBusy ? '…' : t('clientRoosterUitnodigingen.afwijzen')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="table-wrap client-rooster-scroll">
        <div className="client-rooster-grid">
          {days.map(day => {
            const dayLessons = lessonsForDay(day);
            const isToday = isSameDay(day, now);
            return (
              <div key={day.toISOString()} className={`client-rooster-day${isToday ? ' is-today' : ''}`}>
                <div className="client-rooster-day-head">
                  <span>{format(day, 'EEE', { locale: nl })}</span>
                  <strong>{format(day, 'd')}</strong>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dayLessons.length === 0 ? (
                    <div style={{
                      padding: '16px 12px',
                      background: 'var(--color-bg)',
                      borderRadius: 'var(--radius)',
                      textAlign: 'center',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                    }}>
                      —
                    </div>
                  ) : (
                    dayLessons.map(les => {
                      // PT-afspraken en nog niet ingeplande aanvragen kennen
                      // geen zelf-inschrijving: die lopen via de aanvraagflow
                      // (`/client/afspraken`). Ze krijgen daarom nooit
                      // deelnemersaantallen of in-/uitschrijfknoppen — vóór
                      // Fase 2 gebeurde dat wél, omdat de pagina geen enkel
                      // onderscheid op lestype maakte.
                      const isPT = les.training_kind === 'pt';
                      const isRequest = les.is_request === true;
                      const enrolled = enrollmentCounts[les.lesson_id] || 0;
                      const isFull = enrolled >= les.max_participants;
                      const myStatus = myEnrollments[les.id];
                      const isEnrolled = myStatus === 'enrolled';
                      const isWaitlisted = myStatus === 'waitlisted';
                      const busy = actionLoading === les.lesson_id;
                      const isVerlopen = new Date(les.starts_at) < now;
                      const accent = isPT ? '#7c3aed'
                        : isEnrolled ? 'var(--color-primary)'
                        : isWaitlisted ? '#f59e0b'
                        : 'var(--color-border)';
                      const titel = isRequest
                        ? t('clientRooster.ptAanvraag')
                        : (les.name || t('clientRooster.ptLabel'));

                      return (
                        <article
                          key={les.id}
                          className="client-training-card"
                          style={{
                            '--training-accent': accent,
                            opacity: isVerlopen ? 0.7 : 1,
                          }}
                          role="button"
                          tabIndex={0}
                          onClick={() => openLessonDetails(les)}
                          onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openLessonDetails(les);
                            }
                          }}
                        >
                          {(isPT || les.training_kind === 'workshop') && (
                            <div style={{
                              display: 'inline-block', marginBottom: 4,
                              padding: '1px 6px', borderRadius: 99,
                              fontSize: 10, fontWeight: 700,
                              background: isPT ? '#7c3aed22' : '#0891b222',
                              color: isPT ? '#6d28d9' : '#0e7490',
                            }}>
                              {isPT ? t('clientRooster.ptLabel') : t('clientRooster.workshopLabel')}
                            </div>
                          )}
                          <div className="client-training-title">
                            <span>{titel}</span>
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>
                            {toAmsterdamTime(les.starts_at)} · {les.duration_minutes} {t('clientRooster.min')}
                          </div>
                          {!!lesCoachNaam(les) && (
                            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>
                              👤 {lesCoachNaam(les)}
                            </div>
                          )}
                          {les.location?.name && (
                            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 6 }}>
                              📍 {les.location.name}
                            </div>
                          )}

                          {isRequest ? (
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#9a3412' }}>
                              {les.request_status === 'rescheduled'
                                ? t('clientRooster.ptAanvraagRescheduled')
                                : t('clientRooster.ptAanvraagPending')}
                            </div>
                          ) : isPT ? (
                            <div style={{ fontSize: 11, fontWeight: 600, color: isVerlopen ? 'var(--color-text-muted)' : '#6d28d9' }}>
                              {isVerlopen ? t('clientRooster.gevolgd') : t('clientRooster.ptGeenInschrijving')}
                            </div>
                          ) : (
                            <>
                              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                                {enrolled}/{les.max_participants} {t('clientRooster.plaatsen')}
                              </div>

                              {isVerlopen ? (
                                <div style={{ fontSize: 11, fontWeight: 600, color: isEnrolled ? 'var(--color-primary)' : 'var(--color-text-muted)' }}>
                                  {isEnrolled ? `✓ ${t('clientRooster.gevolgd')}` : t('clientRooster.verlopen')}
                                </div>
                              ) : (
                                <>
                                  {isEnrolled && (
                                    <div style={{ marginBottom: 6 }}>
                                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-primary)', display: 'block', marginBottom: 4 }}>
                                        ✓ {t('clientRooster.ingeschreven')}
                                      </span>
                                      <button
                                        className="btn btn-ghost btn-sm"
                                        style={{ width: '100%', fontSize: 11 }}
                                        onClick={event => { event.stopPropagation(); handleUnenroll(les.lesson_id); }}
                                        disabled={busy}
                                      >
                                        {busy ? '…' : t('clientRooster.uitschrijven')}
                                      </button>
                                    </div>
                                  )}

                                  {isWaitlisted && (
                                    <div style={{ marginBottom: 6 }}>
                                      <span style={{ fontSize: 11, fontWeight: 600, color: '#f59e0b', display: 'block', marginBottom: 4 }}>
                                        ⏳ {les.my_waitlist_position
                                          ? t('clientRooster.opWachtlijstPositie', { positie: les.my_waitlist_position })
                                          : t('clientRooster.opWachtlijst')}
                                      </span>
                                      <button
                                        className="btn btn-ghost btn-sm"
                                        style={{ width: '100%', fontSize: 11 }}
                                        onClick={event => { event.stopPropagation(); handleUnenroll(les.lesson_id); }}
                                        disabled={busy}
                                      >
                                        {busy ? '…' : t('clientRooster.uitschrijven')}
                                      </button>
                                    </div>
                                  )}

                                  {!myStatus && !isBlocked && (
                                    isFull ? (
                                      <button
                                        className="btn btn-secondary btn-sm"
                                        style={{ width: '100%', fontSize: 11 }}
                                        onClick={event => { event.stopPropagation(); handleEnroll(les.lesson_id); }}
                                        disabled={busy}
                                      >
                                        {busy ? '…' : t('clientRooster.opWachtlijst')}
                                      </button>
                                    ) : (
                                      <button
                                        className="btn btn-primary btn-sm"
                                        style={{ width: '100%', fontSize: 11 }}
                                        onClick={event => { event.stopPropagation(); handleEnroll(les.lesson_id); }}
                                        disabled={busy}
                                      >
                                        {busy ? '…' : t('clientRooster.inschrijven')}
                                      </button>
                                    )
                                  )}
                                </>
                              )}
                            </>
                          )}
                          <span className="client-training-more">{t('clientRooster.bekijkDetails')} <ChevronRight size={13} /></span>
                        </article>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}
      {selectedLesson && (() => {
        const les = selectedLesson;
        const isPT = les.training_kind === 'pt';
        const isRequest = les.is_request === true;
        const enrolled = enrollmentCounts[les.lesson_id] || 0;
        const myStatus = myEnrollments[les.id];
        const isEnrolled = myStatus === 'enrolled';
        const isWaitlisted = myStatus === 'waitlisted';
        const isFull = enrolled >= les.max_participants;
        const busy = actionLoading === les.lesson_id;
        const isVerlopen = new Date(les.starts_at) < now;
        const coach = les.executing_coach || les.coach;
        const locationLines = trainingLocationLines(les);
        const cancelDeadline = new Date(new Date(les.starts_at).getTime() - (les.cancel_deadline_hours || 0) * 3600000);
        const deadlinePassed = cancelDeadline < now;
        const buddyNames = les.buddy_names || [];
        return (
          <div className="client-training-modal-backdrop" onMouseDown={() => setSelectedLesson(null)} role="presentation">
            <section className="client-training-modal" role="dialog" aria-modal="true" aria-labelledby="training-detail-title" onMouseDown={event => event.stopPropagation()}>
              <div className="client-training-modal-hero">
                <div>
                  <span className="client-training-modal-kicker">{isPT ? t('clientRooster.ptLabel') : les.training_kind === 'workshop' ? t('clientRooster.workshopLabel') : t('clientRooster.groepslesLabel')}</span>
                  <h2 id="training-detail-title">{isRequest ? t('clientRooster.ptAanvraag') : (les.name || t('clientRooster.ptLabel'))}</h2>
                  <p>{format(toAmsterdamDate(les.starts_at), 'EEEE d MMMM', { locale: nl })} · {toAmsterdamTime(les.starts_at)}</p>
                </div>
                <button className="client-training-modal-close" onClick={() => setSelectedLesson(null)} aria-label={t('clientRooster.detailsSluiten')}><X size={20} /></button>
              </div>
              <div className="client-training-modal-body">
                {detailLoading && <div className="client-training-detail-loading"><div className="spinner" /> {t('clientRooster.detailsLaden')}</div>}
                {detailError && <div className="error-msg">{detailError}</div>}
                <div className="client-training-facts">
                  <div><CalendarDays size={18} /><span><small>{t('clientRooster.wanneer')}</small>{format(toAmsterdamDate(les.starts_at), 'd MMMM yyyy', { locale: nl })}</span></div>
                  <div><Clock3 size={18} /><span><small>{t('clientRooster.duur')}</small>{les.duration_minutes} {t('clientRooster.min')}</span></div>
                  {!!lesCoachNaam(les) && <div><UserRound size={18} /><span><small>{t('clientRooster.coach')}</small>{lesCoachNaam(les)}</span></div>}
                  {locationLines.length > 0 && <div><MapPin size={18} /><span><small>{t('clientRooster.locatie')}</small>{locationLines[0]}</span></div>}
                  {!isPT && !isRequest && <div><UsersRound size={18} /><span><small>{t('clientRooster.beschikbaarheid')}</small>{enrolled}/{les.max_participants} {t('clientRooster.plaatsen')}</span></div>}
                </div>
                {coach && (
                  <div className="client-training-coach-card">
                    {coach.avatar_url ? <img src={coach.avatar_url} alt="" /> : <span className="client-training-avatar-fallback">{coach.full_name?.trim()?.[0]?.toUpperCase() || '?'}</span>}
                    <div>
                      <small>{t('clientRooster.jouwCoach')}</small>
                      <h3>{coach.full_name}</h3>
                      <p>{coach.bio?.trim() || t('clientRooster.coachGeenIntro')}</p>
                    </div>
                  </div>
                )}
                <div className="client-training-description">
                  <h3>{t('clientRooster.overTraining')}</h3>
                  <p>{les.description?.trim() || t('clientRooster.geenOmschrijving')}</p>
                </div>
                {locationLines.length > 0 && (
                  <div className="client-training-location-card">
                    {les.location?.image_url && <img src={les.location.image_url} alt="" />}
                    <div><small>{t('clientRooster.locatie')}</small>{locationLines.map(line => <span key={line}>{line}</span>)}</div>
                  </div>
                )}
                {!!les.bring_items?.trim() && <div className="client-training-note"><strong>{t('clientRooster.meenemen')}</strong><span>{les.bring_items.trim()}</span></div>}
                {!isRequest && (
                  <div className="client-training-policy-grid">
                    <div>
                      <AlarmClock size={18} />
                      <span><small>{t('clientRooster.annuleerbeleid')}</small>{deadlinePassed
                        ? t('clientRooster.annuleertermijnVerstreken')
                        : les.cancel_deadline_hours > 0
                          ? t('clientRooster.annulerenTot', { moment: format(cancelDeadline, 'd MMM HH:mm', { locale: nl }) })
                          : t('clientRooster.annulerenTotStart')}</span>
                    </div>
                    <div>
                      <Tag size={18} />
                      <span><small>{t('clientRooster.prijs')}</small>{les.credit_cost > 0
                        ? t('clientRooster.prijsCredits', { count: les.credit_cost })
                        : t('clientRooster.gratis')}</span>
                    </div>
                  </div>
                )}
                {buddyNames.length > 0 && (
                  <div className="client-training-buddies">
                    <small>{t('clientRooster.buddiesIngeschreven')}</small>
                    {buddyNames.map(name => (
                      <div key={name}><span>{name.trim().charAt(0).toUpperCase()}</span>{t('clientRooster.buddyDoetMee', { naam: name })}</div>
                    ))}
                  </div>
                )}
              </div>
              <div className="client-training-modal-footer">
                <span>{isRequest ? t('clientRooster.ptAanvraagPending') : isEnrolled ? t('clientRooster.ingeschreven') : isWaitlisted ? t('clientRooster.opWachtlijst') : isVerlopen ? t('clientRooster.verlopen') : `${les.credit_cost || 0} ${t('clientRooster.credits')}`}</span>
                {!isPT && !isRequest && !isVerlopen && !isBlocked && (isEnrolled || isWaitlisted ? (
                  <button className="btn btn-ghost" disabled={busy} onClick={() => handleUnenroll(les.lesson_id)}>{busy ? '…' : t('clientRooster.uitschrijven')}</button>
                ) : (
                  <button className="btn btn-primary" disabled={busy} onClick={() => handleEnroll(les.lesson_id)}>{busy ? '…' : isFull ? t('clientRooster.opWachtlijst') : t('clientRooster.inschrijven')}</button>
                ))}
              </div>
            </section>
          </div>
        );
      })()}
    </div>
  );
}
